import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import "../css/AuthModal.css";
import GoogleAuthButton from "./GoogleAuthButton";
import { setDoc, doc, getDoc } from "firebase/firestore";
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword } from "firebase/auth";
import { firestore } from "../util/Firebase";
import { FOOTER_CONFIG } from "../constants/FooterConfig";
import { getUserExperimentId } from "../util/ExpirmentHelper";
import { getDefaultUserProgress } from "../util/UserProgressHelper";
import { trackEvent, TRACKING_EVENTS } from "../util/TrackingHelper";
import { useGlobalState } from "../GlobalState";
import { BCIcon, ICONS } from "./generic/BCIcons";
import { FlexCol, FlexRow } from "./generic/Containers";
import {Body1, Body3, Caption} from "./generic/BCHeaders";
import { notifyUserEmailEntered } from "../util/APIHelper";

export const AuthModal: React.FC = () => {
    const navigate = useNavigate();
    const { authModalMode, setShowAuthModal, setAuthModalMode } = useGlobalState();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState<string | null>(null);

    const isLogin = authModalMode === 'login';

    const handleGoogleSuccess = async (user: any) => {
        let isReturningUser = false;
        try {
            const userDocRef = doc(firestore, "users", user.uid);
            const userDoc = await getDoc(userDocRef);

            isReturningUser = userDoc.exists() && userDoc.data()?.experimentId;
            const experimentId = isReturningUser ? userDoc.data()?.experimentId : getUserExperimentId();
            if (isReturningUser) {
                // TODO Do we care about this?
            } else {
                trackEvent(TRACKING_EVENTS.NEW_USER_SIGNS_UP, { 
                    user_id: user.uid, 
                    email: user.email,
                    signup_method: 'google'
                });
            }

            if (!isReturningUser) {
                const userData = {
                    ...getDefaultUserProgress(user.uid, experimentId),
                    uid: user.uid,
                    email: user.email,
                    name: user.displayName || "Anonymous",
                    provider: "google",
                    photoUrl: user.photoURL || null,
                    experimentId: experimentId,
                    hasOnboarded: false,
                    isPremium: false
                };

                await setDoc(userDocRef, userData, { merge: true });
                
                // Notify backend of new user email
                if (user.email) {
                    notifyUserEmailEntered(user.email, experimentId);
                }
            }

        } catch (error) {
            console.error("Error saving user data: ", error);
        }
        setShowAuthModal(false);
        // Redirect new users to onboarding, returning users to dashboard
        if (!isReturningUser) {
            navigate("/onboarding");
        } else {
            navigate("/");
        }
    };

    const handleGoogleError = (error: any) => {
        console.error("Google sign-in error:", error);
    };

    const handleAuth = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const auth = getAuth();

        try {
            if (isLogin) {
                // Handle login
                await signInWithEmailAndPassword(auth, email, password);
                setShowAuthModal(false);
                navigate("/");
            } else {
                // Handle sign up
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                const user = userCredential.user;

                const userDocRef = doc(firestore, "users", user.uid);
                const userDoc = await getDoc(userDocRef);

                const isReturningUser = userDoc.exists() && userDoc.data()?.experimentId
                const experimentId = isReturningUser ? userDoc.data()?.experimentId : getUserExperimentId();
                if (isReturningUser) {
                    // TODO Do we care about this?
                } else {
                    // Track signup across all platforms including Google Ads (value 1)
                    trackEvent(TRACKING_EVENTS.NEW_USER_SIGNS_UP, { 
                        user_id: user.uid, 
                        email: user.email || "",
                        signup_method: 'email'
                    });
                }

                if (!isReturningUser) {
                    const userData = {
                        ...getDefaultUserProgress(user.uid, experimentId),
                        uid: user.uid,
                        email: user.email,
                        name: user.displayName || "Anonymous",
                        provider: "email",
                        experimentId: experimentId,
                        hasOnboarded: false,
                        isPremium: false
                    };

                    await setDoc(userDocRef, userData, { merge: true });
                    
                    // Notify backend of new user email
                    if (user.email) {
                        notifyUserEmailEntered(user.email, experimentId);
                    }
                }

                setShowAuthModal(false);
                // Redirect new users to onboarding
                if (!isReturningUser) {
                    navigate("/onboarding");
                } else {
                    navigate("/");
                }
            }
        } catch (error: any) {
            console.error("Error during email/password auth: ", error);
            setError(error.message);
        }
    };

    const switchMode = () => {
        setError(null);
        
        // Track the button click based on what mode they're switching TO
        if (isLogin) {
            // Currently in login mode, switching to register
            trackEvent(TRACKING_EVENTS.REGISTER_CLICKED, {
                source: 'auth_modal_switch'
            });
        } else {
            // Currently in register mode, switching to login
            trackEvent(TRACKING_EVENTS.LOGIN_CLICKED, {
                source: 'auth_modal_switch'
            });
        }
        
        setAuthModalMode(isLogin ? 'register' : 'login');
    };

    const header = isLogin ? "Welcome Back!" : "Welcome!";
    const subheader = isLogin ? "Sign in to continue your journey" : "Sign up for free and let's build your Tech Career";
    const ctaText = isLogin ? "Sign In" : "Sign Up";

    return (
        <div className="auth-modal-overlay" onClick={() => setShowAuthModal(false)}>
            <FlexCol 
                className="auth-modal-content" 
                onClick={(e) => e.stopPropagation()}
                maxWidth={28}
                widthPercent={90}
                borderRadius={1.5}
                padding={2}
                pVertical={3.5}
                align={"center"}
                gap={2}
            >
                <BCIcon icon={ICONS.CLOSE} className="auth-modal-close-button" onClick={() => setShowAuthModal(false)}/>
                {/*<img*/}
                {/*    className="auth-modal-logo"*/}
                {/*    src={LogoMedium}*/}
                {/*    alt="Beyond Code Logo"*/}
                {/*    onClick={() => {*/}
                {/*        setShowAuthModal(false);*/}
                {/*        navigate("/");*/}
                {/*    }}*/}
                {/*/>*/}
                <FlexCol align="center" gap={.5}>
                    {!isLogin && <h3 className={'modal-login-header'} style={{textAlign: 'center'}}>
                        Your First Step to a <span style={{color: 'var(--primary-blue)'}}>Dev Job</span>
                    </h3>}
                    {isLogin && <h3 className={'modal-login-header'} style={{textAlign: 'center'}}>
                        Welcome Back!
                    </h3>}
                    {!isLogin && <Body1 text="Join 2,500+ developers getting hired with Beyond Code" textAlign="center" style={{fontSize: '.9rem'}}/>}
                    <p className="auth-modal-cta-text">
                        Create your <strong>free account</strong> to get started
                    </p>
                    {/*{!isLogin && <Body1 text="  " textAlign="center" style={{fontSize: '.9rem', marginTop: '1em'}}/>}*/}
                    {isLogin && <Body1 text="Sign in to continue your journey" textAlign="center" />}

                    <FlexCol widthPercent={100} maxWidth={18} gap={1} mTop={.5}>
                        <GoogleAuthButton onSuccess={handleGoogleSuccess} onError={handleGoogleError} />
                        
                        <FlexRow align="center" gap={1}>
                            <div className="divider-line" />
                            <Caption text="or" />
                            <div className="divider-line" />
                        </FlexRow>
                        
                        <form onSubmit={handleAuth}>
                            <FlexCol gap={1}>
                                <div className="form-group">
                                    <label htmlFor="email" className="visuallyhidden">Email</label>
                                    <input
                                        id="email"
                                        type="email"
                                        placeholder="Enter your email"
                                        className="input-field"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        aria-required="true"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="password" className="visuallyhidden">Password</label>
                                    <input
                                        id="password"
                                        type="password"
                                        placeholder="Enter your password"
                                        className="input-field"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        aria-required="true"
                                    />
                                </div>
                                <button type="submit" className="primary-btn" id="auth-submit-button">
                                    {ctaText}
                                </button>
                                {error && <Body1 text={error} className="error-message" />}
                            </FlexCol>
                        </form>
                        
                        <FlexCol className="auth-modal-mode-switch" gap={0.8} align="center">
                            {!isLogin && (
                                <FlexRow gap={.2}>
                                    <Body1 text="Already have an account?"/>
                                    <button className="mode-switch-button" onClick={switchMode}>Log in</button>
                                </FlexRow>
                            )}
                            {isLogin && (
                                <FlexRow gap={.2}>
                                    <Body1 text="Don't have an account?"/>
                                    <button className="mode-switch-button" onClick={switchMode}>Sign up</button>
                                </FlexRow>
                            )}
                            <p className="terms-text">
                                By continuing, you agree to our <a href={FOOTER_CONFIG.privacyUrl}>Privacy Policy</a> and <a href={FOOTER_CONFIG.termsUrl}>Terms of Use</a>.
                            </p>
                        </FlexCol>
                    </FlexCol>
                </FlexCol>
            </FlexCol>
        </div>
    );
}; 